import { Day, Stop } from "@city-sage/core-client";
import { Button } from "@city-sage/aurum-avis-labs-components";
import { ChevronDown, ChevronRight, MoreHorizontal, Clock, MapPin } from "lucide-react";
import { getDayColor } from "../../lib/constants/dayColors";

interface ItineraryDaySectionProps {
  day: Day
  dayIndex: number
  stops: Stop[]
  currentSection?: { id: string }
  onStopClick?: (stopId: string) => void
}

export const ItineraryDaySection = ({ 
  day, 
  dayIndex, 
  stops,
  onStopClick 
}: ItineraryDaySectionProps) => {
  const dayColorConfig = getDayColor(day.number - 1);
  const dayColor = dayColorConfig.bgClass;
  

  return (
    <div className={`w-full min-h-full py-2 px-1 transition-all duration-700`}>
      <div className="flex items-center justify-center gap-3 mb-4">
        <div className="text-center">
          <h3 className="text-xl font-bold">{day?.title || `Day ${day.number}`}</h3>
          <p className="text-gray-600">Day {day.number}</p>
        </div>
      </div>

      <div className="space-y-4">
        <h4 className="font-semibold text-center">{stops?.length || 0} Stops</h4>

        {/* Handle many stops differently */}
        {!stops || stops.length === 0 ? (
          <div className="p-4 bg-gray-50 rounded-lg text-gray-500">
            <MapPin className="w-6 h-6 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No stops planned for this day</p>
          </div>
        ) : stops.length <= 4 ? (
          // Show all stops for days with 4 or fewer stops
          <div className="space-y-2">
            {stops.map((stop, index) => (
              <button 
                key={stop.id} 
                onClick={() => onStopClick?.(stop.id)}
                className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg text-sm hover:bg-gray-100 transition-colors w-full text-left group"
              >
                <div
                  className={`w-6 h-6 rounded-full ${dayColor} flex items-center justify-center text-white text-xs font-semibold`}
                >
                  {index + 1}
                </div>
                <div className="text-left flex-1">
                  <div className="font-medium">{stop.title || `Stop ${index + 1}`}</div>
                  <div className="text-xs text-gray-600 flex items-center gap-1">
                    {stop.durationMinutes && (
                      <>
                        <Clock className="w-3 h-3" />
                        <span>{stop.durationMinutes} min</span>
                      </>
                    )}
                    {stop.place.address?.city && (
                      <>
                        <MapPin className="w-3 h-3" />
                        <span>{stop.place.address.city}</span>
                      </>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0" />
              </button>
            ))}
          </div>
        ) : (
          // Show condensed view for days with many stops
          <div className="space-y-2">
            {stops.slice(0, 3).map((stop, index) => (
              <button 
                key={stop.id} 
                onClick={() => onStopClick?.(stop.id)}
                className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg text-sm hover:bg-gray-100 transition-colors w-full text-left group"
              >
                <div
                  className={`w-6 h-6 rounded-full ${dayColor} flex items-center justify-center text-white text-xs font-semibold`}
                >
                  {index + 1}
                </div>
                <div className="text-left flex-1">
                  <div className="font-medium">{stop.title || `Stop ${index + 1}`}</div>
                  <div className="text-xs text-gray-600 flex items-center gap-1">
                    {stop.durationMinutes && (
                      <>
                        <Clock className="w-3 h-3" />
                        <span>{stop.durationMinutes} min</span>
                      </>
                    )}
                    {stop.place.address?.city && (
                      <>
                        <MapPin className="w-3 h-3" />
                        <span>{stop.place.address.city}</span>
                      </>
                    )}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0" />
              </button>
            ))}
            <div className="flex items-center gap-2 p-2 bg-gray-100 rounded-lg text-sm">
              <div className="w-6 h-6 rounded-full bg-gray-400 flex items-center justify-center">
                <MoreHorizontal className="w-3 h-3 text-white" />
              </div>
              <div className="text-gray-600">and {stops.length - 3} more stops</div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}